import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Checkbox,
  Divider,
} from '@mui/material';
import { WarningAmberRounded } from '@mui/icons-material';
import { DuplicateSessionInfo, DuplicateEntry, dupKey } from '../utils/duplicateSession';

interface Props {
  open: boolean;
  info: DuplicateSessionInfo | null;
  /** Shown on the confirm button while the retry request is in flight. */
  loading?: boolean;
  /**
   * When false the list is read-only and confirming re-sends the whole batch.
   * When true (default) each collision has a checkbox — unchecked ones are left
   * out of the retry. `onConfirm` always receives the unchecked entries.
   */
  selectable?: boolean;
  /**
   * Total sessions the operator asked to create (new + duplicates). When given,
   * the dialog shows a running breakdown of exactly what "confirm" will do.
   */
  totalRequested?: number;
  onCancel: () => void;
  onConfirm: (skip: DuplicateEntry[]) => void;
}

const MAX_SHOWN = 20;

const parseIST = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

const fmtDate = (d: Date) =>
  d.toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

const fmtTime = (d: Date) =>
  d.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: 'numeric',
    minute: '2-digit',
  });

const plural = (n: number) => (n === 1 ? '' : 's');

/** One line of the compact summary box. */
const LedgerRow: React.FC<{
  label: string;
  value: number | string;
  strong?: boolean;
  muted?: boolean;
  tone?: 'default' | 'success' | 'warning';
}> = ({ label, value, strong, muted, tone = 'default' }) => {
  const color =
    tone === 'success'
      ? 'success.main'
      : tone === 'warning'
      ? 'warning.main'
      : muted
      ? 'text.secondary'
      : 'text.primary';
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.35 }}>
      <Typography variant="body2" color={color} fontWeight={strong ? 700 : 400}>
        {label}
      </Typography>
      <Typography variant="body2" color={color} fontWeight={strong ? 700 : 600}>
        {value}
      </Typography>
    </Box>
  );
};

/**
 * Confirmation shown when the backend reports that a create would duplicate an
 * existing session (same date, time and class). Non-duplicate sessions in the
 * same request are always created; for the duplicates the operator picks which
 * to create anyway (checked) and which to skip (unchecked). The retry carries
 * `allowDuplicate: true` plus the skipped list.
 */
const DuplicateSessionConfirmDialog: React.FC<Props> = ({
  open,
  info,
  loading,
  selectable = true,
  totalRequested,
  onCancel,
  onConfirm,
}) => {
  const duplicates = info?.duplicates ?? [];
  const dupCount = duplicates.length;
  const shown = duplicates.slice(0, MAX_SHOWN);
  const canSelect = selectable && dupCount > 0;

  // Keys the operator has unchecked — these are excluded from the retry.
  const [skip, setSkip] = React.useState<Set<string>>(new Set());

  // Fresh dialog (new conflict) starts with everything checked.
  React.useEffect(() => {
    setSkip(new Set());
  }, [info]);

  const toggle = (key: string) =>
    setSkip((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const shownKeys = shown.map((d) => dupKey(d.classId, d.date));
  const allShownSkipped = shownKeys.length > 0 && shownKeys.every((k) => skip.has(k));
  const setAll = (skipThem: boolean) => setSkip(skipThem ? new Set(shownKeys) : new Set());

  const skippedCount = skip.size;
  const keptDupCount = dupCount - skippedCount;

  const total =
    typeof totalRequested === 'number' && totalRequested >= dupCount ? totalRequested : null;
  const newCount = total != null ? total - dupCount : null;
  const willCreateCount = (newCount ?? 0) + keptDupCount;
  const knowTotal = canSelect && total != null;
  const nothingToCreate = knowTotal && willCreateCount === 0;
  const isMixed = newCount != null && newCount > 0;

  // Only shown as a fallback when the summary box is not (read-only / unknown total).
  const intro = info?.message || 'A session already exists at this date and time.';

  const confirmLabel = knowTotal
    ? nothingToCreate
      ? 'Nothing to create'
      : `Create ${willCreateCount} session${plural(willCreateCount)}`
    : skippedCount === 0
    ? 'Create anyway'
    : skippedCount >= dupCount
    ? 'Skip all duplicates'
    : `Create the rest (${keptDupCount})`;

  const handleConfirm = () =>
    onConfirm(duplicates.filter((d) => skip.has(dupKey(d.classId, d.date))));

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxWidth: 520 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberRounded color="warning" />
        {isMixed ? 'Some sessions already exist' : 'Duplicate session'}
      </DialogTitle>
      <DialogContent>
        {!knowTotal && (
          <DialogContentText sx={{ mb: dupCount ? 2 : 0 }}>{intro}</DialogContentText>
        )}

        {knowTotal && (
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, px: 1.75, py: 1.25, mb: 2 }}>
            {newCount! > 0 && (
              <LedgerRow label="New sessions" value={newCount as number} tone="success" />
            )}
            <LedgerRow label="Duplicates kept" value={keptDupCount} />
            <LedgerRow label="Duplicates skipped" value={skippedCount} muted />
            <Divider sx={{ my: 0.75 }} />
            <LedgerRow
              label="Total to create"
              value={willCreateCount}
              strong
              tone={nothingToCreate ? 'warning' : 'default'}
            />
          </Box>
        )}

        {!!dupCount && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
                Duplicates ({dupCount})
              </Typography>
              {canSelect && shown.length > 1 && (
                <Button
                  size="small"
                  variant="text"
                  disabled={loading}
                  onClick={() => setAll(!allShownSkipped)}
                  sx={{ textTransform: 'none', minWidth: 0 }}
                >
                  {allShownSkipped ? 'Keep all' : 'Skip all'}
                </Button>
              )}
            </Box>

            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1.5,
                overflow: 'hidden',
                maxHeight: 320,
                overflowY: 'auto',
              }}
            >
              {shown.map((d, i) => {
                const key = dupKey(d.classId, d.date);
                const dt = parseIST(d.date);
                const willSkip = skip.has(key);
                return (
                  <React.Fragment key={key + i}>
                    {i > 0 && <Divider />}
                    <Box
                      onClick={canSelect && !loading ? () => toggle(key) : undefined}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 1.5,
                        py: 1.25,
                        cursor: canSelect && !loading ? 'pointer' : 'default',
                        transition: 'background-color 120ms',
                        opacity: willSkip ? 0.5 : 1,
                        '&:hover': canSelect && !loading ? { bgcolor: 'action.hover' } : undefined,
                      }}
                    >
                      {canSelect && (
                        <Checkbox
                          edge="start"
                          size="small"
                          tabIndex={-1}
                          checked={!willSkip}
                          disabled={loading}
                          sx={{ p: 0.5 }}
                        />
                      )}
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          noWrap
                          sx={{ textDecoration: willSkip ? 'line-through' : 'none' }}
                        >
                          {d.className}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {dt ? `${fmtDate(dt)} · ${fmtTime(dt)}` : d.date}
                          {(d as any).location ? ` · ${(d as any).location}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>

            {dupCount > MAX_SHOWN && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                …and {dupCount - MAX_SHOWN} more{canSelect ? ' (kept)' : ''}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="warning"
          variant="contained"
          disabled={loading || nothingToCreate}
        >
          {loading ? <CircularProgress size={18} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateSessionConfirmDialog;
